import type { ValidatedPricingBenefit } from '../loyalty/benefit-authority.ts';
import type { PricingAdjustment } from '../order/order-finalization.ts';
import type { QuoteResult } from './quote.ts';

const clone = <T>(value: T): T => structuredClone(value);

const assertReference = (value: string): string => {
  const accepted = value?.trim();
  if (!accepted) throw new Error('PRICING_BENEFIT_REFERENCE_REQUIRED');
  return accepted;
};

const productBasisMinor = (
  quote: Extract<QuoteResult, { kind: 'quoted' }>,
  productIds: readonly string[],
): number => {
  const eligible = new Set(productIds);
  const basis = quote.evidence.pricing.lines
    .filter((line) => eligible.has(line.productId))
    .reduce((sum, line) => sum + line.lineSubtotalMinor, 0);
  if (!Number.isSafeInteger(basis) || basis <= 0) throw new Error('PRICING_BENEFIT_SCOPE_EMPTY');
  return basis;
};

const validateBenefit = (benefit: ValidatedPricingBenefit): void => {
  assertReference(benefit.referenceId);
  if (benefit.stacking !== 'stackable' && benefit.stacking !== 'exclusive') {
    throw new Error('PRICING_BENEFIT_STACKING_INVALID');
  }
  if (benefit.scope.kind !== 'order' && benefit.scope.kind !== 'products') {
    throw new Error('PRICING_BENEFIT_SCOPE_INVALID');
  }
  if (benefit.scope.kind === 'products' && benefit.scope.productIds.length === 0) {
    throw new Error('PRICING_BENEFIT_SCOPE_INVALID');
  }
  if (benefit.kind === 'fixed') {
    if (!Number.isSafeInteger(benefit.amountMinor) || benefit.amountMinor <= 0) {
      throw new Error('PRICING_BENEFIT_AMOUNT_INVALID');
    }
    return;
  }
  if (!Number.isSafeInteger(benefit.basisPoints) || benefit.basisPoints <= 0 || benefit.basisPoints > 10_000) {
    throw new Error('PRICING_BENEFIT_PERCENTAGE_INVALID');
  }
};

export function applyValidatedBenefitsToQuote(
  input: QuoteResult,
  benefits: readonly ValidatedPricingBenefit[],
): QuoteResult {
  if (input.kind !== 'quoted' || benefits.length === 0) return clone(input);

  const quote = clone(input);
  const seen = new Set<string>();
  for (const benefit of benefits) {
    validateBenefit(benefit);
    const referenceId = assertReference(benefit.referenceId);
    if (seen.has(referenceId)) throw new Error('PRICING_BENEFIT_DUPLICATE');
    seen.add(referenceId);
  }

  const existingAdjustments = quote.evidence.pricing.adjustments;
  if (benefits.some((benefit) => benefit.stacking === 'exclusive')) {
    if (benefits.length > 1 || existingAdjustments.length > 0) {
      throw new Error('PRICING_BENEFIT_STACKING_CONFLICT');
    }
  }

  let currentTotalMinor = quote.evidence.pricing.totalMinor;
  const appended: PricingAdjustment[] = [];

  for (const benefit of benefits) {
    const basisMinor = benefit.scope.kind === 'order'
      ? currentTotalMinor
      : productBasisMinor(quote, benefit.scope.productIds);

    const requestedDiscountMinor = benefit.kind === 'fixed'
      ? benefit.amountMinor
      : Math.floor((basisMinor * benefit.basisPoints) / 10_000);

    if (!Number.isSafeInteger(requestedDiscountMinor) || requestedDiscountMinor < 0) {
      throw new Error('PRICING_BENEFIT_AMOUNT_INVALID');
    }
    const discountMinor = Math.min(requestedDiscountMinor, basisMinor, currentTotalMinor);
    if (discountMinor <= 0) throw new Error('PRICING_BENEFIT_ZERO_VALUE');

    appended.push({
      kind: 'discount',
      referenceId: benefit.referenceId,
      amountMinor: -discountMinor,
    });
    currentTotalMinor -= discountMinor;
  }

  if (!Number.isSafeInteger(currentTotalMinor) || currentTotalMinor < 0) {
    throw new Error('PRICING_TOTAL_INVALID');
  }

  return {
    ...quote,
    evidence: {
      ...quote.evidence,
      validation: clone(quote.evidence.validation),
      pricing: {
        ...quote.evidence.pricing,
        totalMinor: currentTotalMinor,
        lines: quote.evidence.pricing.lines.map((line) => ({ ...line })),
        adjustments: [...quote.evidence.pricing.adjustments.map((adjustment) => clone(adjustment)), ...appended],
      },
    },
  };
}
